import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { UserAvatar } from '@/components/UserAvatar';
import { MobileMenuTrigger } from '@/components/Layout';
import { LeaderboardSkeleton } from '@/components/Skeleton';
import { 
  Trophy, TrendingUp, TrendingDown, Minus, 
  ChevronUp, CheckCircle, Medal, Flame, Crown, Filter, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const Leaderboard = ({ user }) => {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [filters, setFilters] = useState({ cities: [], teams: [] });

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity, selectedTeam]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilter === 'city' && selectedCity) params.city = selectedCity;
      if (activeFilter === 'team' && selectedTeam) params.team = selectedTeam;
      
      const response = await api.getRegularLeaderboard(params);
      setLeaderboardData(response.data);
      setFilters(response.data.filters || { cities: [], teams: [] });
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setSelectedCity('');
      setSelectedTeam('');
    }
  };

  // Standardized currency formatting
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    if (amount >= 10000000) {
      const crValue = amount / 10000000;
      return `₹${crValue % 1 === 0 ? crValue.toFixed(0) : parseFloat(crValue.toFixed(2))}Cr`;
    }
    if (amount >= 100000) {
      const lValue = amount / 100000;
      return `₹${lValue % 1 === 0 ? lValue.toFixed(0) : parseFloat(lValue.toFixed(1))}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Movement icon with context
  const getMovementDisplay = (movement, size = 'sm') => {
    const iconSize = size === 'lg' ? 14 : 12;
    if (movement > 0) {
      return (
        <span className="flex items-center gap-0.5 text-emerald-600 text-xs font-semibold" title={`↑ ${movement} rank vs yesterday`}>
          <TrendingUp size={iconSize} />
          <span>{movement}</span>
        </span>
      );
    } else if (movement < 0) {
      return (
        <span className="flex items-center gap-0.5 text-[#5B3DF5] text-xs font-semibold" title={`↓ ${Math.abs(movement)} rank vs yesterday`}>
          <TrendingDown size={iconSize} />
          <span>{Math.abs(movement)}</span>
        </span>
      );
    }
    return (
      <span className="flex items-center text-slate-300 text-xs" title="No change">
        <Minus size={iconSize} />
      </span>
    );
  };

  // Get trend arrow for Your Rank section
  const getTrendArrow = (movement) => {
    if (movement > 0) return <span className="text-emerald-500 font-bold ml-1">↑{movement}</span>;
    if (movement < 0) return <span className="text-[#5B3DF5] font-bold ml-1">↓{Math.abs(movement)}</span>;
    return <span className="text-slate-400 font-medium ml-1">→</span>;
  };

  const leaderboard = leaderboardData?.leaderboard || [];
  const myRank = leaderboardData?.my_rank;
  const top3 = leaderboard.slice(0, 3);
  const restOfLeaderboard = leaderboard; // Show all rankings, not just 4+
  const scope = leaderboardData?.scope || 'Pan India';

  // Get top performer for the day
  const topPerformerToday = top3[0] ? formatCurrency(top3[0].net_sales) : '₹0';

  // Show skeleton while loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA]">
        <div className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="skeleton w-10 h-10 rounded-full" />
            <div className="skeleton w-32 h-6 rounded" />
          </div>
        </div>
        <LeaderboardSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 md:pb-8 page-fade-in">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 md:px-8 md:py-4 header-blur sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Back Button - Always visible */}
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="page-back-btn"
            >
              <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-[#111827]">Leaderboard</h1>
              <p className="text-xs text-slate-400 mt-0.5 hidden sm:block">Monthly performance rankings</p>
            </div>
          </div>
          {/* Hamburger Menu - Mobile */}
          <MobileMenuTrigger user={user} />
        </div>
      </div>

      {/* Main Content Container - Responsive */}
      <div className="px-4 md:px-8 py-4 md:py-6">
        <div className="max-w-[1400px] mx-auto space-y-4 md:space-y-6">
          
          {/* TOP SECTION: Total Sales + Filters (Full Width) */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Sales Summary */}
            {leaderboardData && (
              <Card data-testid="leaderboard-header" className="p-4 md:p-5 bg-white rounded-2xl shadow-sm flex-1 lg:max-w-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide font-medium">Total Sales</p>
                    <p className="text-2xl md:text-3xl font-bold text-[#111827] tabular-nums mt-1">
                      {formatCurrency(leaderboardData.total_approved_sales)}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-[10px] text-slate-400">
                        Approved net sales • MTD • {scope}
                      </p>
                      {leaderboardData.total_reps_contributing > 0 && (
                        <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-medium">
                          {leaderboardData.total_reps_contributing} rep{leaderboardData.total_reps_contributing !== 1 ? 's' : ''} contributing
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                    <Trophy size={24} className="text-amber-600 md:w-7 md:h-7" />
                  </div>
                </div>
              </Card>
            )}

            {/* Filter Chips */}
            <div data-testid="filter-section" className="flex gap-2 flex-wrap items-center">
              <div className="hidden lg:flex items-center gap-2 text-slate-400 mr-2">
                <Filter size={16} />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeFilter === 'all'
                    ? 'bg-[#111827] text-white shadow-md'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                }`}
              >
                Pan India
              </button>
              <select
                value={activeFilter === 'city' ? selectedCity : ''}
                onChange={(e) => {
                  setActiveFilter('city');
                  setSelectedCity(e.target.value);
                  setSelectedTeam('');
                }}
                className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-sm font-semibold appearance-none pr-8 transition-all duration-200 cursor-pointer ${
                  activeFilter === 'city'
                    ? 'bg-[#111827] text-white shadow-md'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                }`}
              >
                <option value="">City</option>
                {filters.cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <select
                value={activeFilter === 'team' ? selectedTeam : ''}
                onChange={(e) => {
                  setActiveFilter('team');
                  setSelectedTeam(e.target.value);
                  setSelectedCity('');
                }}
                className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-sm font-semibold appearance-none pr-8 transition-all duration-200 cursor-pointer ${
                  activeFilter === 'team'
                    ? 'bg-[#111827] text-white shadow-md'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                }`}
              >
                <option value="">Team</option>
                {filters.teams.map((team) => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Information Banner */}
          <div className="flex items-center gap-2 bg-emerald-50/60 border border-emerald-100 rounded-xl px-3 py-2 md:py-2.5 text-xs text-emerald-700">
            <CheckCircle size={14} className="flex-shrink-0 text-emerald-500" />
            <p>Rankings based on <strong>approved net sales</strong> only.</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto mb-4"></div>
              <p className="text-slate-500 text-sm">Loading leaderboard...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-2xl shadow-sm">
              <Trophy size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 font-medium">No approved sales data available</p>
              <p className="text-slate-400 text-sm mt-1">Start closing deals to appear on the leaderboard</p>
            </Card>
          ) : (
            <>
              {/* MIDDLE SECTION: Your Rank + Podium (2-column on desktop) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* My Rank Card */}
                {myRank && (
                  <Card data-testid="my-rank-card" className="p-4 md:p-5 bg-white rounded-2xl shadow-sm border-l-4 border-l-[#111827]">
                    <div className="flex items-center gap-4">
                      <UserAvatar 
                        name={myRank.rep_name} 
                        imageUrl={myRank.profile_image_url}
                        size="lg"
                        className="ring-2 ring-slate-200"
                      />
                      <div className="flex-1">
                        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1">Your Rank</p>
                        <div className="flex items-baseline">
                          <span className="text-3xl md:text-4xl font-black text-[#111827]">#{myRank.rank}</span>
                          {getTrendArrow(myRank.movement)}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Net Sales</p>
                        <p className="text-xl md:text-2xl font-bold text-[#111827] tabular-nums">
                          {formatCurrency(myRank.net_sales)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Gap to #1 Enhancement */}
                    <div className="mt-3 md:mt-4 pt-3 border-t border-slate-100">
                      {myRank.rank === 1 ? (
                        myRank.is_tied_for_first ? (
                          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-2.5 md:p-3">
                            <Flame size={16} className="text-orange-500" />
                            <span className="text-sm font-semibold">
                              You're tied for #1! {myRank.tied_at_first > 2 && `(${myRank.tied_at_first} reps)`}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-xl p-2.5 md:p-3">
                            <Crown size={18} className="text-amber-500" />
                            <span className="text-sm font-semibold">
                              You're leading! <span className="text-emerald-600">{formatCurrency(myRank.lead_over_second || 0)}</span> ahead of #2
                            </span>
                          </div>
                        )
                      ) : myRank.gap_to_first === 0 ? (
                        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-2.5 md:p-3">
                          <Flame size={16} className="text-orange-500" />
                          <span className="text-sm font-semibold">
                            You're tied for #{myRank.rank}!
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-slate-700 bg-amber-50 border border-amber-100 rounded-xl p-2.5 md:p-3">
                          <Flame size={16} className="text-orange-500" />
                          <span className="text-sm">
                            <span className="font-bold text-[#111827]">{formatCurrency(myRank.gap_to_first || 0)}</span> more to reach #1
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Top 3 Podium - Handles ties properly */}
                {top3.length > 0 && (
                  <Card data-testid="podium" className="p-4 md:p-5 bg-white rounded-2xl shadow-sm">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-4 text-center">Top Performers</p>
                    
                    {/* Check if top 2 are tied for #1 */}
                    {top3[1] && top3[0]?.rank === top3[1]?.rank ? (
                      // Both tied for #1 - Show side-by-side as co-champions
                      <div className="flex items-end justify-center gap-4 md:gap-6">
                        {/* First #1 */}
                        <div className="flex flex-col items-center flex-1 max-w-[100px] md:max-w-[110px]">
                          <div className="relative">
                            <div className="absolute inset-0 bg-amber-300 rounded-full blur-lg opacity-40 scale-125"></div>
                            <UserAvatar 
                              name={top3[0]?.rep_name}
                              imageUrl={top3[0]?.profile_image_url}
                              size="xl"
                              className="ring-3 ring-amber-400 ring-offset-2 relative z-10"
                            />
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg z-20">
                              <span className="text-xs">🥇</span>
                            </div>
                          </div>
                          <div className="mt-2 text-center">
                            <p className="text-xs font-bold text-[#111827] truncate w-full">
                              {top3[0]?.rep_name?.split(' ')[0]}
                            </p>
                            <p className="text-base font-black text-emerald-600 tabular-nums mt-0.5">
                              {formatCurrency(top3[0]?.net_sales)}
                            </p>
                          </div>
                          <div className="mt-2 w-full h-16 md:h-20 bg-gradient-to-t from-amber-200 to-amber-100 border-2 border-amber-300 rounded-t-xl flex flex-col items-center justify-center shadow-inner">
                            <span className="text-xl md:text-2xl font-black text-amber-600">1</span>
                            <span className="text-[9px] font-semibold text-amber-500 -mt-1">TIE</span>
                          </div>
                        </div>
                        
                        {/* Second #1 (tied) */}
                        <div className="flex flex-col items-center flex-1 max-w-[100px] md:max-w-[110px]">
                          <div className="relative">
                            <div className="absolute inset-0 bg-amber-300 rounded-full blur-lg opacity-40 scale-125"></div>
                            <UserAvatar 
                              name={top3[1]?.rep_name}
                              imageUrl={top3[1]?.profile_image_url}
                              size="xl"
                              className="ring-3 ring-amber-400 ring-offset-2 relative z-10"
                            />
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg z-20">
                              <span className="text-xs">🥇</span>
                            </div>
                          </div>
                          <div className="mt-2 text-center">
                            <p className="text-xs font-bold text-[#111827] truncate w-full">
                              {top3[1]?.rep_name?.split(' ')[0]}
                            </p>
                            <p className="text-base font-black text-emerald-600 tabular-nums mt-0.5">
                              {formatCurrency(top3[1]?.net_sales)}
                            </p>
                          </div>
                          <div className="mt-2 w-full h-16 md:h-20 bg-gradient-to-t from-amber-200 to-amber-100 border-2 border-amber-300 rounded-t-xl flex flex-col items-center justify-center shadow-inner">
                            <span className="text-xl md:text-2xl font-black text-amber-600">1</span>
                            <span className="text-[9px] font-semibold text-amber-500 -mt-1">TIE</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Standard podium layout (no tie for #1)
                      <div className="flex items-end justify-center gap-3 md:gap-4">
                        {/* 2nd Place - only show if exists and not tied for #1 */}
                        {top3[1] ? (
                          <div className="flex flex-col items-center flex-1 max-w-[90px] md:max-w-[100px]">
                            <div className="relative opacity-90">
                              <UserAvatar 
                                name={top3[1]?.rep_name}
                                imageUrl={top3[1]?.profile_image_url}
                                size="lg"
                                className="ring-2 ring-slate-300 ring-offset-2"
                              />
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-slate-400 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-[10px] font-bold text-white">🥈</span>
                              </div>
                            </div>
                            <div className="mt-2 text-center">
                              <p className="text-[11px] font-semibold text-slate-600 truncate w-full">
                                {top3[1]?.rep_name?.split(' ')[0]}
                              </p>
                              <p className="text-sm font-bold text-slate-800 tabular-nums mt-0.5">
                                {formatCurrency(top3[1]?.net_sales)}
                              </p>
                            </div>
                            <div className="mt-2 w-full h-12 md:h-14 bg-gradient-to-t from-slate-200 to-slate-100 rounded-t-xl flex items-center justify-center">
                              <span className="text-lg md:text-xl font-bold text-slate-500">{top3[1]?.rank}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center flex-1 max-w-[90px] md:max-w-[100px] opacity-30">
                            <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-200 rounded-full" />
                            <div className="mt-2 w-full h-12 md:h-14 bg-slate-100 rounded-t-xl flex items-center justify-center">
                              <span className="text-lg md:text-xl font-bold text-slate-300">2</span>
                            </div>
                          </div>
                        )}

                        {/* 1st Place - Elevated */}
                        <div className="flex flex-col items-center flex-1 max-w-[100px] md:max-w-[110px] -mt-3">
                          <div className="relative">
                            <div className="absolute inset-0 bg-amber-300 rounded-full blur-lg opacity-40 scale-125"></div>
                            <UserAvatar 
                              name={top3[0]?.rep_name}
                              imageUrl={top3[0]?.profile_image_url}
                              size="xl"
                              className="ring-3 ring-amber-400 ring-offset-2 relative z-10"
                            />
                            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg z-20">
                              <span className="text-xs">🥇</span>
                            </div>
                          </div>
                          <div className="mt-2 text-center">
                            <p className="text-xs font-bold text-[#111827] truncate w-full">
                              {top3[0]?.rep_name?.split(' ')[0]}
                            </p>
                            <p className="text-base font-black text-emerald-600 tabular-nums mt-0.5">
                              {formatCurrency(top3[0]?.net_sales)}
                            </p>
                          </div>
                          <div className="mt-2 w-full h-16 md:h-20 bg-gradient-to-t from-amber-200 to-amber-100 border-2 border-amber-300 rounded-t-xl flex items-center justify-center shadow-inner">
                            <span className="text-xl md:text-2xl font-black text-amber-600">1</span>
                          </div>
                        </div>

                        {/* 3rd Place - only show if exists */}
                        {top3[2] ? (
                          <div className="flex flex-col items-center flex-1 max-w-[90px] md:max-w-[100px]">
                            <div className="relative opacity-90">
                              <UserAvatar 
                                name={top3[2]?.rep_name}
                                imageUrl={top3[2]?.profile_image_url}
                                size="lg"
                                className="ring-2 ring-amber-500 ring-offset-2"
                              />
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center shadow-sm">
                                <span className="text-[10px] font-bold text-white">🥉</span>
                              </div>
                            </div>
                            <div className="mt-2 text-center">
                              <p className="text-[11px] font-semibold text-slate-600 truncate w-full">
                                {top3[2]?.rep_name?.split(' ')[0]}
                              </p>
                              <p className="text-sm font-bold text-slate-800 tabular-nums mt-0.5">
                                {formatCurrency(top3[2]?.net_sales)}
                              </p>
                            </div>
                            <div className="mt-2 w-full h-9 md:h-10 bg-gradient-to-t from-amber-200 to-amber-100 rounded-t-xl flex items-center justify-center">
                              <span className="text-base md:text-lg font-bold text-amber-700">{top3[2]?.rank}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center flex-1 max-w-[90px] md:max-w-[100px] opacity-30">
                            <div className="w-12 h-12 md:w-14 md:h-14 bg-amber-100 rounded-full" />
                            <div className="mt-2 w-full h-9 md:h-10 bg-amber-50 rounded-t-xl flex items-center justify-center">
                              <span className="text-base md:text-lg font-bold text-amber-200">3</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                )}
              </div>

              {/* BOTTOM SECTION: Full Rankings List (Wide Table Style) */}
              <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 md:px-5 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-700">Full Rankings</h3>
                </div>
                
                {/* Desktop Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2.5 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-4">Representative</div>
                  <div className="col-span-2">City</div>
                  <div className="col-span-2">Team</div>
                  <div className="col-span-2 text-right">Net Sales</div>
                  <div className="col-span-1 text-right">Trend</div>
                </div>
                
                <div data-testid="leaderboard-list" className="divide-y divide-slate-100">
                  {restOfLeaderboard.map((rep) => (
                    <div
                      key={rep.rep_id}
                      data-testid={`leaderboard-rank-${rep.rank}`}
                      className="px-4 md:px-5 py-3 md:py-3.5 hover:bg-slate-50 transition-colors"
                    >
                      {/* Mobile Layout */}
                      <div className="md:hidden flex items-center gap-3">
                        <div className="w-10 text-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-400">#{rep.rank}</span>
                          {rep.is_tied && (
                            <span className="ml-0.5 px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-600 rounded">T</span>
                          )}
                        </div>
                        <UserAvatar 
                          name={rep.rep_name}
                          imageUrl={rep.profile_image_url}
                          size="md"
                          className="ring-1 ring-slate-200"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#111827] truncate">{rep.rep_name}</p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {rep.city}{rep.team ? ` • ${rep.team}` : ''}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold text-[#111827] tabular-nums">
                            {formatCurrency(rep.net_sales)}
                          </p>
                        </div>
                        <div className="w-8 flex-shrink-0 flex justify-end">
                          {getMovementDisplay(rep.movement)}
                        </div>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-1 flex items-center gap-1">
                          <span className="text-sm font-bold text-slate-400">#{rep.rank}</span>
                          {rep.is_tied && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-600 rounded">Tie</span>
                          )}
                        </div>
                        <div className="col-span-4 flex items-center gap-3">
                          <UserAvatar 
                            name={rep.rep_name}
                            imageUrl={rep.profile_image_url}
                            size="sm"
                            className="ring-1 ring-slate-200"
                          />
                          <p className="text-sm font-semibold text-[#111827] truncate">{rep.rep_name}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-slate-600">{rep.city || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-slate-600 truncate">{rep.team || '-'}</p>
                        </div>
                        <div className="col-span-2 text-right">
                          <p className="text-sm font-bold text-[#111827] tabular-nums">
                            {formatCurrency(rep.net_sales)}
                          </p>
                          {rep.gap_to_above > 0 && (
                            <p className="text-[10px] text-slate-400 font-medium">
                              ▼ {formatCurrency(rep.gap_to_above)} from #{rep.rank - 1}
                            </p>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {getMovementDisplay(rep.movement, 'lg')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
